import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { ALL_COUNTRIES, COUNTRY_BY_NAME } from '../lib/countries';

/**
 * Searchable country picker. Stores the country NAME (string, e.g. "Germany")
 * in `value` and exposes both name + ISO-2 code in `onChange(name, code)`.
 *
 * - Full ~250 ISO-3166 country list with flag emojis
 * - Type to filter, Enter to pick first match, Esc to close
 * - Renders a custom popup so it works identically across Chrome/FF/Safari/Brave
 */
export default function CountryPicker({
  value, onChange, error, placeholder = 'Select your country', testid = 'country-picker',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const selected = value ? COUNTRY_BY_NAME[value.toLowerCase()] : null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ALL_COUNTRIES;
    return ALL_COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().startsWith(q),
    );
  }, [query]);

  const pick = (c) => {
    onChange(c.name, c.code);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-toggle`}
        className={`w-full flex items-center justify-between gap-2 px-3 py-3 rounded-lg border bg-white text-left text-sm transition focus:outline-none focus:ring-2 ${
          error ? 'border-red-400 focus:ring-red-400' : 'border-slate-300 focus:ring-cyan-500'
        }`}
      >
        {selected ? (
          <span className="flex items-center gap-2 truncate">
            <span className="text-xl leading-none">{selected.flag}</span>
            <span className="truncate text-slate-800">{selected.name}</span>
          </span>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 bg-white rounded-lg shadow-2xl border border-slate-200 max-h-80 overflow-hidden flex flex-col"
          data-testid={`${testid}-dropdown`}
        >
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
                  if (e.key === 'Enter' && filtered.length) { e.preventDefault(); pick(filtered[0]); }
                }}
                placeholder="Search 250 countries…"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid={`${testid}-search`}
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="p-4 text-center text-sm text-slate-400">No country matches “{query}”.</p>
            ) : (
              filtered.map((c) => {
                const active = selected && selected.code === c.code;
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => pick(c)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-cyan-50 ${
                      active ? 'bg-cyan-50 font-semibold text-cyan-700' : 'text-slate-700'
                    }`}
                    data-testid={`${testid}-option-${c.code}`}
                  >
                    <span className="text-xl leading-none">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-[11px] font-mono text-slate-400">{c.code}</span>
                    {active && <Check className="w-4 h-4 text-cyan-600" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
